import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToaster } from '@squareup/shared-ui-react';
import { FulfillmentType, type LocationFulfillmentEnablementUpdates } from './useFulfillmentConfiguration.js';

export function usePrepTimeSaveSuccessToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback(() => {
    showToast({
      text: t('prepTime.toast.success'),
      variant: 'success',
      persistent: false,
    });
  }, [showToast, t]);
}

export function usePrepTimeSaveErrorToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback(() => {
    showToast({
      text: t('prepTime.toast.error'),
      variant: 'critical',
      persistent: false,
    });
  }, [showToast, t]);
}

export function useFulfillmentEnablementSaveSuccessToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback((fulfillmentType: FulfillmentType, locationsToUpdate: LocationFulfillmentEnablementUpdates) => {
    const fulfillmentTypeKey = fulfillmentType === FulfillmentType.Pickup ? 'pickup' : 'delivery';

    let message: string;
    if (Object.keys(locationsToUpdate).length === 1) {
      const enabled = locationsToUpdate[Object.keys(locationsToUpdate)[0]];
      message = enabled
        ? fulfillmentTypeKey === 'pickup' ? t('profileDropdown.pickup.enabled.toast.success') : t('profileDropdown.delivery.enabled.toast.success')
        : fulfillmentTypeKey === 'pickup' ? t('profileDropdown.pickup.disabled.toast.success') : t('profileDropdown.delivery.disabled.toast.success');
    } else {
      message = fulfillmentTypeKey === 'pickup' ? t('profileDropdown.pickup.enablement.toast.multiple.success') : t('profileDropdown.delivery.enablement.toast.multiple.success');
    }

    showToast({
      text: message,
      variant: 'success',
      persistent: false,
    });
  }, [showToast, t]);
}

export function useFulfillmentEnablementSaveErrorToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback((fulfillmentType: FulfillmentType) => {
    const fulfillmentTypeKey = fulfillmentType === FulfillmentType.Pickup ? 'pickup' : 'delivery';
    showToast({
      text: fulfillmentTypeKey === 'pickup' ? t('profileDropdown.pickup.enablement.toast.error') : t('profileDropdown.delivery.enablement.toast.error'),
      variant: 'critical',
      persistent: false,
    });
  }, [showToast, t]);
}

export function useOrderingConfigurationSuccessToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback((isOrderingPaused: boolean, updatedCount: number) => {
    let message: string;

    if (updatedCount > 1) {
      message = t('profileDropdown.ordering.toast.statusUpdatedMultiple', { count: updatedCount });
    } else {
      message = isOrderingPaused
        ? t('profileDropdown.ordering.toast.turnedOff')
        : t('profileDropdown.ordering.toast.turnedOn');
    }

    showToast({
      text: message,
      variant: 'success',
      persistent: false,
    });
  }, [showToast, t]);
}

export function useOrderingConfigurationErrorToast() {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  return useCallback(() => {
    showToast({
      text: t('profileDropdown.ordering.toast.error'),
      variant: 'critical',
      persistent: false,
    });
  }, [showToast, t]);
}
